/**
 * Validation and upload helpers for the panel image-upload widget.
 */

export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"]
// The two names browsers give an .ico file; a field opts in to them (a favicon).
export const ICON_IMAGE_TYPES = ["image/x-icon", "image/vnd.microsoft.icon"]
export const MAX_IMAGE_SIZE = 10 * 1024 * 1024

const REQUEST_TIMEOUT_MS = 60000

const TYPE_LABELS = {
  "image/jpeg": "JPEG",
  "image/png": "PNG",
  "image/gif": "GIF",
  "image/webp": "WebP",
  "image/x-icon": "ICO",
  "image/vnd.microsoft.icon": "ICO"
}

// "JPEG, PNG, GIF or WebP" for the given MIME types, in their order.
function typesSentence(types) {
  const labels = [...new Set(types.map((type) => TYPE_LABELS[type] || type))]
  if (labels.length < 2) return labels[0] || ""
  return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`
}

// Some browsers report no type for an .ico; its name decides, and the server
// still checks the bytes.
function acceptedType(file, acceptedTypes) {
  if (acceptedTypes.includes(file.type)) return true
  const takesIcons = ICON_IMAGE_TYPES.some((type) => acceptedTypes.includes(type))
  return takesIcons && !file.type && /\.ico$/i.test(file.name || "")
}

/**
 * Validate a candidate file before uploading.
 * @param {File} file
 * @param {number} maxSize - Maximum allowed size in bytes
 * @param {string[]} acceptedTypes - The MIME types this field takes
 * @returns {string|null} An error message, or null when the file is acceptable
 */
export function validateImageFile(file, maxSize = MAX_IMAGE_SIZE, acceptedTypes = ACCEPTED_IMAGE_TYPES) {
  if (!file) return "No file selected."
  if (!acceptedType(file, acceptedTypes)) {
    return `Only ${typesSentence(acceptedTypes)} images can be uploaded.`
  }
  if (file.size > maxSize) {
    return `Image must be under ${Math.round(maxSize / (1024 * 1024))}MB.`
  }
  return null
}

export function getCsrfToken() {
  const meta = document.querySelector("meta[name='csrf-token']")
  return meta ? meta.content : ""
}

/**
 * Upload an image file as multipart form data.
 * @param {string} url - The upload endpoint
 * @param {File} file - The image to upload
 * @param {Object} context - Owner params, e.g. { article_id: "..." }
 * @returns {Promise<Object>} Server response body, or { error } on failure
 */
export async function uploadImage(url, file, context = {}) {
  const formData = new FormData()
  formData.append("file", file)
  Object.entries(context).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      formData.append(key, value)
    }
  })

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": getCsrfToken(),
        Accept: "application/json"
      },
      body: formData,
      signal: controller.signal
    })

    const body = await response.json().catch(() => ({}))
    if (!response.ok) {
      return { error: body.error || `Upload failed (${response.status})` }
    }
    return body
  } catch {
    return { error: "Upload failed. Check your connection and try again." }
  } finally {
    clearTimeout(timeout)
  }
}

/**
 * Fetch the site's uploaded image library for the given owner context.
 * @param {string} url - The library endpoint
 * @param {Object} context - Owner params, e.g. { page_id: "..." }
 * @returns {Promise<Array|Object>} An array of assets, or { error } on failure
 */
export async function fetchLibrary(url, context = {}) {
  const query = new URLSearchParams()
  Object.entries(context).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      query.append(key, value)
    }
  })

  try {
    const response = await fetch(`${url}?${query.toString()}`, {
      headers: { Accept: "application/json" }
    })
    if (!response.ok) {
      return { error: `Could not load the library (${response.status}).` }
    }
    return await response.json()
  } catch {
    return { error: "Could not load the library. Check your connection and try again." }
  }
}

/**
 * Open a modal picker over a set of library assets.
 * @param {Array} assets - Assets with { url, preview_url, filename }
 * @param {Function} onSelect - Called with the chosen asset
 * @param {Function} [onClose] - Called whenever the modal closes (select, Escape, backdrop)
 * @returns {Function} A close function that removes the modal and its listeners
 */
export function openLibraryPicker(assets, onSelect, onClose) {
  const overlay = document.createElement("div")
  overlay.className = "fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
  overlay.setAttribute("role", "dialog")
  overlay.setAttribute("aria-modal", "true")

  const dialog = document.createElement("div")
  dialog.className = "w-full max-w-3xl max-h-[80vh] overflow-y-auto rounded-lg bg-white p-4 shadow-xl"
  overlay.appendChild(dialog)

  const heading = document.createElement("h2")
  heading.className = "mb-3 text-lg font-medium text-gray-800"
  heading.textContent = "Choose an image"
  dialog.appendChild(heading)

  const onKeydown = (event) => {
    if (event.key === "Escape") close()
  }
  let closed = false
  const close = () => {
    if (closed) return
    closed = true
    document.removeEventListener("keydown", onKeydown)
    overlay.remove()
    onClose?.()
  }

  if (!Array.isArray(assets) || assets.length === 0) {
    const empty = document.createElement("p")
    empty.className = "py-8 text-center text-sm text-gray-500"
    empty.textContent = "No images have been uploaded to this site yet."
    dialog.appendChild(empty)
  } else {
    const grid = document.createElement("div")
    grid.className = "grid grid-cols-3 gap-3 sm:grid-cols-4"
    assets.forEach((asset) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "overflow-hidden rounded-lg border border-gray-200 hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      button.title = asset.filename || ""
      const img = document.createElement("img")
      img.src = asset.preview_url || asset.url
      img.alt = asset.filename || ""
      img.loading = "lazy"
      img.className = "h-24 w-full bg-gray-50 object-cover"
      button.appendChild(img)
      button.addEventListener("click", () => {
        onSelect(asset)
        close()
      })
      grid.appendChild(button)
    })
    dialog.appendChild(grid)
  }

  overlay.addEventListener("click", (event) => {
    if (event.target === overlay) close()
  })
  document.addEventListener("keydown", onKeydown)
  document.body.appendChild(overlay)

  return close
}
